/* eslint-disable */
// hooks/useCybersecurityFilters.ts
import { useMemo } from "react";
import { useLazyEffect } from "../../../../hooks/useLazyEffect";
import { useTypedSelector } from "../../../../hooks/useTypedSelector";
import { useActions } from "../../../../hooks/useActions";
type propertiesNames =
  | "company_name"
  | "audit_consulting"
  | "company_size"
  | "company_type"
  | "distributors"
  | "ethical_hacking"
  | "courses"
  | "higher_education_institution"
  | "b2b_training"
  | "has_certifications"
  | "has_other_services"
  | "id"
  | "integrators"
  | "mssp"
  | "other_services"
  | "region"
  | "services"
  | "registration_date"; // 1970-01-01

// JSON stringified array, e.g. ["..."] | "services_cb" | "startup" | "training_cb" | "vendors";
const inScalar = (prop: string, values: (string | number)[]) =>
  values.length ? ["in", ["get", prop], ["literal", values]] : [];

// 2) "any of" for array property of feature (e.g., feature.properties.company_type: string[])
const anyInArrayProp = (prop: string, values: (string | number)[]) =>
  values.length
    ? ["any", ...values.map((v) => [">=", ["index-of", v, ["get", prop]], 0])]
    : [];

// 3) Equality for booleans
const eqBool = (prop: string, value?: boolean) =>
  typeof value === "boolean" ? ["==", ["get", prop], value] : [];

// 4) Range by ISO date (>= / <=)
const gteDate = (prop: string, iso?: string) =>
  iso ? [">=", ["get", prop], iso] : [];
const lteDate = (prop: string, iso?: string) =>
  iso ? ["<=", ["get", prop], iso] : [];

// 5) Range by numbers (example for future)
const gteNum = (prop: string, n?: number) =>
  typeof n === "number" ? [">=", ["get", prop], n] : [];
const lteNum = (prop: string, n?: number) =>
  typeof n === "number" ? ["<=", ["get", prop], n] : [];

const useCybersecurityFilters = () => {
  const { offset } = useTypedSelector((s) => s.mapInterface);

  const {
    activeSuggestions,
    company_size, // string[]
    registration_date__gte, // ISO string | ""
    registration_date__lte, // ISO string | ""
    company_type,
    services,
    service_category,
    other_services, // string | undefined
    certifications, // boolean | undefined
  } = useTypedSelector((s) => s.cybersecurityMapFilters);
  // const { hash_ids } = useTypedSelector((s) => s.mapCybersecurityInfo);
  const { getCybersecurityList: getDefaultCybersecurityResult } = useActions();

  // const filterByCodes = inScalar("id", hash_ids);
  const suggestionsFilters = useMemo(() => {
    const filtersByType = {
      keywords: [] as string[],
      edrpou: [] as string[],
      regions: [] as string[],
    };

    activeSuggestions.forEach(({ type, code }) => {
      if (type === "Ключове слово") {
        filtersByType.keywords.push(code);
      }

      if (type === "Юридична особа") {
        filtersByType.edrpou.push(code);
      }

      if (type === "Регіон") {
        filtersByType.regions.push(code);
      }
    });

    return filtersByType;
  }, [activeSuggestions]);

  const filterByRegion = inScalar("region", suggestionsFilters.regions);
  const filterByEdrpou = inScalar("edrpou", suggestionsFilters.edrpou);

  const keywordFilters = suggestionsFilters.keywords.map((keyword) => [
    "in",
    keyword,
    ["get", "company_name"],
  ]);

  const filterByKeyword = keywordFilters.length
    ? ["any", ...keywordFilters]
    : [];

  // Company type:
  const filterByCompanyType = anyInArrayProp("company_type", company_type);

  // Company size
  const filterByCompanySize = inScalar("company_size", company_size);

  // Services:
  const filterByServices = anyInArrayProp("services", services);
  const filterByOtherServices = anyInArrayProp(
    "other_services",
    other_services ?? []
  );
  // If only JSON-string in "services", correct filtering is not possible.

  // Dates (assume properties.registration_date is ISO string)
  const filterByRegDateGTE = gteDate(
    "registration_date",
    registration_date__gte || undefined
  );
  const filterByRegDateLTE = lteDate(
    "registration_date",
    registration_date__lte || undefined
  );

  // Booleans
  // const filterByHasOtherServices = other_services
  //   ? eqBool("has_other_services", other_services)
  //   : [];
  const filterByHasCertifications = certifications
    ? eqBool("has_certifications", certifications)
    : [];

  const filterServicesCb = service_category.includes("services_cb")
    ? eqBool("services_cb", true)
    : [];
  const filterIntegrators = service_category.includes("integrators")
    ? eqBool("integrators", true)
    : [];
  const filterVendors = service_category.includes("vendors")
    ? eqBool("vendors", service_category.includes("vendors"))
    : [];

  const filterDistributors = service_category.includes("distributors")
    ? eqBool("distributors", service_category.includes("distributors"))
    : [];

  const filterTrainingCb = service_category.includes("training_cb")
    ? eqBool("training_cb", service_category.includes("training_cb"))
    : [];
  const filterStartup = service_category.includes("startup")
    ? eqBool("startup", service_category.includes("startup"))
    : [];

  const filterAssociation = service_category.includes("association")
    ? eqBool("association", service_category.includes("association"))
    : [];

  const filterGovernmentOrganization = service_category.includes(
    "government_organization"
  )
    ? eqBool(
        "government_organization",
        service_category.includes("government_organization")
      )
    : [];

  const filterEthicalHacking = service_category.includes("ethical_hacking")
    ? eqBool("ethical_hacking", service_category.includes("ethical_hacking"))
    : [];

  const filterMssp = service_category.includes("mssp")
    ? eqBool("mssp", service_category.includes("mssp"))
    : [];
  const filterAuditConsulting = service_category.includes("audit_consulting")
    ? eqBool("audit_consulting", service_category.includes("audit_consulting"))
    : [];
  const filterCourses = service_category.includes("courses")
    ? eqBool("courses", true)
    : [];
  const filterHigherEducationInstitution = service_category.includes(
    "higher_education_institution"
  )
    ? eqBool("higher_education_institution", true)
    : [];
  const filterB2BTraining = service_category.includes("b2b_training")
    ? eqBool("b2b_training", true)
    : [];

  const rawFilters: any[] = [
    filterByKeyword,
    filterByRegion,
    filterByEdrpou,
    filterByServices,
    filterByRegDateGTE,
    filterByRegDateLTE,
    filterByCompanySize,
    filterByHasCertifications,
    filterByOtherServices,
    filterByCompanyType,
    // service categories:
    filterServicesCb,
    filterIntegrators,
    filterVendors,
    filterDistributors,
    filterTrainingCb,
    filterStartup,
    filterAssociation,
    filterGovernmentOrganization,
    filterEthicalHacking,
    filterMssp,
    filterAuditConsulting,
    filterCourses,
    filterHigherEducationInstitution,
    filterB2BTraining,
  ];

  const filters = rawFilters.filter((f) => Array.isArray(f) && f.length > 0);

  // if (suggestionsFilters.edrpou.length && filterByCodes.length) {
  //   filters.push(filterByCodes);
  // }

  useLazyEffect(() => {
    getDefaultCybersecurityResult();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    offset,
    activeSuggestions,
    company_size,
    registration_date__gte,
    registration_date__lte,
    company_type,
    services,
    other_services,
    certifications,
  ]);

  const mapFilter = useMemo<any>(
    () => (filters.length ? ["all", ...filters] : ["all"]),
    [filters]
  );

  return mapFilter;
};

export default useCybersecurityFilters;
